import { useEffect, useId, useRef, useState } from 'react';

/**
 * A small ⓘ button that toggles a popover explaining what the current admin
 * page is for. Rendered by `AdminShell` next to the breadcrumb; the copy comes
 * from the `admin:hints.*` registry (see `lib/pageHints.ts`).
 *
 * Accessibility: the button carries a translated `label` (its accessible name),
 * `aria-expanded`, and `aria-controls`; the popover dismisses on Escape and on
 * click/focus outside.
 */
export interface PageHintButtonProps {
  /** The hint sentence shown in the popover. */
  readonly text: string;
  /** Translated accessible name for the button (e.g. "About this page"). */
  readonly label: string;
}

export function PageHintButton({ text, label }: PageHintButtonProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointerDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative inline-flex">
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-controls={popoverId}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-ink-3 transition-colors duration-[120ms] hover:bg-surface-2 hover:text-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/25"
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4M12 8h.01" />
        </svg>
      </button>
      {open ? (
        <div
          id={popoverId}
          role="tooltip"
          className="absolute start-0 top-7 z-50 w-72 rounded-lg border border-hairline bg-surface p-3 text-sm leading-snug text-ink-2 shadow-lg"
        >
          {text}
        </div>
      ) : null}
    </div>
  );
}
